import React, {useState} from "react";
import {Box, Card, Container, Stack, Typography} from "@mui/material";
import {
    type AgeCategory,
    AgeCategoryType,
} from "crqlar-react-common/dist/models/ageCategory/ageCategory.model";
import {AgeCategoriesElement} from "../../common/uiElements/AgeCategoriesElement";
import { GuestsElement } from "../../common/uiElements/GuestsElement";
import { NationalityElement } from "../../common/uiElements/NationalityElement";
import { LanguagesElement } from "../../common/uiElements/LanguagesElement";
import { LanguageElement } from "../../common/uiElements/LanguageElement";
import { DateElement } from "../../common/uiElements/DateElement";
import { AllergiesElement } from "../../common/uiElements/AllergiesElement";
import { DietsElement } from "../../common/uiElements/DietsElement";
import { MedicalElement } from "../../common/uiElements/MedicalElement";
import { StayRangeElement } from "../../common/uiElements/StayRangeElement";
import { SimpleChipElement } from "../../common/uiElements/SimpleChipElement";
import { GenderElement } from "../../common/uiElements/GenderElement";
import { BirthdayElement } from "../../common/uiElements/BirthdayElement";
import { NotesElement } from "../../common/uiElements/NotesElement";
import { RecommendationsInformationElement } from "../../common/uiElements/RecommendationsInformationElement";
import { RecommendationsSuccessElement } from "../../common/uiElements/RecommendationsSuccessElement";
import { RecommendationsWarningElement } from "../../common/uiElements/RecommendationsWarningElement";
import {
    AccommodationElement,
    FacilityElement,
    RestaurantElement,
    RestaurantTableElement,
    StaffElement,
} from "../../common/uiElements/ResourceElement";
import { ReservationStatusElement } from "../../common/uiElements/ReservationStatusElement";
import { ReservationStatus } from "crqlar-react-common/dist/models/reservation/reservation.model";
import {ResourceDisplayKind} from "crqlar-react-common/dist/models/aggregation/aggregation.model";
import { StayStatusElement } from "../../common/uiElements/StayStatusElement";
import {
    ReservationsElement,
    type ReservationElementItem,
} from "../../common/uiElements/ReservationsElement";
import {ElementChip} from "../../common/uiElements/base/ElementChip";

interface TestPageItem {
    componentName: string;
    colored: React.ReactNode;
    cutoff: React.ReactNode;
    details: string;
}

const RESERVATION_DEMO_NOW = new Date("2026-07-15T10:00:00");

const AGE_CATEGORY_DEMO_DATE = new Date("2026-07-21T12:00:00");

const AGE_CATEGORY_DEMO_CATEGORIES: AgeCategory[] = [
    {
        id: "adult",
        name: "Adult",
        minAge: 18,
        type: AgeCategoryType.ADULT,
        alias: null,
        color: null,
    },
    {
        id: "u17",
        name: "Under 17",
        minAge: 13,
        maxAge: 17,
        type: AgeCategoryType.TEENAGER,
        alias: "U17",
        color: "#EF4444",
    },
    {
        id: "u12",
        name: "Under 12",
        minAge: 7,
        maxAge: 12,
        type: AgeCategoryType.CHILD,
        alias: "U12",
        color: "#2563EB",
    },
    {
        id: "child",
        name: "Child",
        minAge: 2,
        maxAge: 6,
        type: AgeCategoryType.CHILD,
        alias: null,
        color: null,
    },
    {
        id: "infant",
        name: "Infant",
        minAge: 0,
        maxAge: 1,
        type: AgeCategoryType.INFANT,
        alias: null,
        color: null,
    },
];

const AGE_CATEGORY_DEMO_BIRTHDAYS = [
    "1981-08-03",
    "1983-09-03",
    "2010-02-08",
    "2012-11-24",
    "2015-09-29",
    "2021-07-01",
    "2025-01-01",
];

const AGE_CATEGORY_DEMO_STATS = {
    adults: 2,
    children: 5,
};

const RESERVATION_STATUS_ITEMS: ReservationElementItem[] = Object.values(ReservationStatus)
    .map((status, index) => ({
        reservationId: `status-${status}`,
        status,
        start: new Date(RESERVATION_DEMO_NOW.getTime() + (index + 1) * 30 * 60 * 1000),
        serviceName: status,
        guestCount: index % 3 + 1,
    }));

const RESERVATION_TIMELINE_ITEMS: ReservationElementItem[] = [
    {
        reservationId: "started",
        status: ReservationStatus.ACTIVE,
        start: new Date("2026-07-15T09:00:00"),
        serviceName: "Classic massage",
        guestCount: 1,
    },
    {
        reservationId: "next-1",
        status: ReservationStatus.CONFIRMED,
        start: new Date("2026-07-15T10:30:00"),
        serviceName: "Deep tissue massage",
        guestCount: 2,
    },
    {
        reservationId: "next-2",
        status: ReservationStatus.PENDING,
        start: new Date("2026-07-15T11:30:00"),
        serviceName: "Facial treatment",
        guestCount: 1,
    },
    {
        reservationId: "next-3",
        status: ReservationStatus.OPTION,
        start: new Date("2026-07-15T12:30:00"),
        serviceName: "Hot stone massage",
        guestCount: 2,
    },
    {
        reservationId: "next-4",
        status: ReservationStatus.CONFIRMED,
        start: new Date("2026-07-15T13:30:00"),
        serviceName: "Manicure",
        guestCount: 1,
    },
    {
        reservationId: "next-5",
        status: ReservationStatus.CONFIRMED,
        start: new Date("2026-07-15T14:30:00"),
        serviceName: "Pedicure",
        guestCount: 1,
    },
];

const RESTAURANT_RESERVATION_ITEM: ReservationElementItem = {
    reservationId: "restaurant-next",
    status: ReservationStatus.CONFIRMED,
    start: new Date("2026-07-15T18:30:00"),
    eatPeriod: "dinner",
    restaurants: [{
        id: "panorama-lounge",
        name: "Panorama Lounge with Rooftop Terrace",
        kind: ResourceDisplayKind.RESTAURANT,
        tagColor: "#0ea5e9",
    }],
    tables: [{
        id: "table-t12",
        name: "T12",
        kind: ResourceDisplayKind.RESTAURANT_TABLE,
    }],
    guestCount: 2,
};

function ElementChipActionsDemo() {
    const [lastAction, setLastAction] = useState("No action yet");

    return (
        <Stack gap={0.75} alignItems="flex-start">
            <Box sx={{display: "flex", flexWrap: "wrap", gap: 0.5}}>
                <ElementChip
                    text="Click action"
                    onClick={() => setLastAction("Body clicked")}
                />
                <ElementChip
                    text="Delete action"
                    onDelete={() => setLastAction("Delete clicked")}
                />
                <ElementChip
                    text="Both actions"
                    onClick={() => setLastAction("Combined chip body clicked")}
                    onDelete={() => setLastAction("Combined chip delete clicked")}
                />
            </Box>
            <Typography variant="caption" color="text.secondary">
                {lastAction}
            </Typography>
        </Stack>
    );
}

const ITEMS: TestPageItem[] = [
    {
        componentName: "ElementChip (actions)",
        details: "Optional body onClick and isolated right-end onDelete; the result appears below the chips",
        colored: <ElementChipActionsDemo/>,
        cutoff: null,
    },
    {
        componentName: "AccommodationElement",
        details: "Luxurious penthouse featuring ocean views and private jacuzzi (Room)",
        colored: <AccommodationElement name="PH3" />,
        cutoff: <AccommodationElement name="PH3" />
    },
    {
        componentName: "StaffElement",
        details: "Senior event organizer and host coordinator (Staff Member)",
        colored: <StaffElement name="Sarah Parker" />,
        cutoff: <StaffElement name="Sarah Parker" />
    },
    {
        componentName: "FacilityElement",
        details: "Relaxing outdoor garden area with hot mineral springs (Facility)",
        colored: <FacilityElement name="Zen Garden" />,
        cutoff: <FacilityElement name="Zen Garden" />
    },
    {
        componentName: "RestaurantElement",
        details: "Gourmet dining experience with panoramic city and sunset views (Restaurant)",
        colored: <RestaurantElement name="Panorama Lounge" />,
        cutoff: <RestaurantElement name="Panorama Lounge" />
    },
    {
        componentName: "RestaurantTableElement",
        details: "A table inherits the color of its parent restaurant",
        colored: <RestaurantTableElement name="T12" parentColor="#f43f5e" />,
        cutoff: <RestaurantTableElement name="T12" parentColor="#f43f5e" />
    },
    {
        componentName: "GuestsElement",
        details: "Stay reservation with guests from every age category",
        colored: <GuestsElement computedAgeCategories={{ adults: 2, teenagers: 1, children: 1, infants: 1 }} />,
        cutoff: <GuestsElement computedAgeCategories={{ adults: 2, teenagers: 1, children: 1, infants: 1 }} />
    },
    {
        componentName: "AgeCategoriesElement",
        details: "Seven guests: 2 adults, 2 U17, 1 U12, 1 child and 1 infant; includes custom aliases/colors and default icons",
        colored: (
            <AgeCategoriesElement
                ageCategories={AGE_CATEGORY_DEMO_CATEGORIES}
                birthdays={AGE_CATEGORY_DEMO_BIRTHDAYS}
                referenceDate={AGE_CATEGORY_DEMO_DATE}
                computedAgeCategories={AGE_CATEGORY_DEMO_STATS}
            />
        ),
        cutoff: (
            <AgeCategoriesElement
                ageCategories={AGE_CATEGORY_DEMO_CATEGORIES}
                birthdays={AGE_CATEGORY_DEMO_BIRTHDAYS}
                referenceDate={AGE_CATEGORY_DEMO_DATE}
                computedAgeCategories={AGE_CATEGORY_DEMO_STATS}
            />
        ),
    },
    {
        componentName: "NationalityElement",
        details: "Austria — has a flag icon",
        colored: <NationalityElement nationality="AT" />,
        cutoff: <NationalityElement nationality="AT" />
    },
    {
        componentName: "NationalityElement",
        details: "Unknown code — falls back to help icon",
        colored: <NationalityElement nationality="XX" />,
        cutoff: <NationalityElement nationality="XX" />
    },
    {
        componentName: "LanguagesElement",
        details: "Single language: French",
        colored: <LanguagesElement languageCodes={["fr"]} />,
        cutoff: <LanguagesElement languageCodes={["fr"]} />
    },
    {
        componentName: "LanguageElement",
        details: "One full language chip: the German flag reads German in English and Deutsch in German",
        colored: <LanguageElement languageCode="de" locale="en" />,
        cutoff: <LanguageElement languageCode="de" locale="en" />
    },
    {
        componentName: "DateElement",
        details: "Full date and time: 14.03.2025 09:26",
        colored: <DateElement date={new Date("2025-03-14T09:26:00")} dateOnly={false} />,
        cutoff: <DateElement date={new Date("2025-03-14T09:26:00")} dateOnly={false} />
    },
    {
        componentName: "AllergiesElement",
        details: "Single food allergy: Shellfish",
        colored: <AllergiesElement allergies={["SHELLFISH"]} />,
        cutoff: <AllergiesElement allergies={["SHELLFISH"]} />
    },
    {
        componentName: "DietsElement",
        details: "Single diet option: Keto",
        colored: <DietsElement diets={["KETO"]} />,
        cutoff: <DietsElement diets={["KETO"]} />
    },
    {
        componentName: "MedicalElement",
        details: "Single medical details: Heart Condition",
        colored: <MedicalElement medical={["HEART_CONDITION"]} />,
        cutoff: <MedicalElement medical={["HEART_CONDITION"]} />
    },
    {
        componentName: "StayRangeElement",
        details: "Stay range pretty formatted: 14. - 21. Mär. 2025",
        colored: <StayRangeElement start={new Date("2025-03-14")} end={new Date("2025-03-21")} locale="de" />,
        cutoff: <StayRangeElement start={new Date("2025-03-14")} end={new Date("2025-03-21")} locale="de" />
    },
    {
        componentName: "SimpleChipElement",
        details: "Generic text chips; empty values render nothing",
        colored: <SimpleChipElement values={["QUIET_ROOM", "HIGH_FLOOR"]}/>,
        cutoff: <SimpleChipElement values={["QUIET_ROOM", "HIGH_FLOOR"]}/>
    },
    {
        componentName: "GenderElement",
        details: "Gender value formatted: Male",
        colored: <GenderElement gender="MALE" />,
        cutoff: <GenderElement gender="MALE" />
    },
    {
        componentName: "BirthdayElement",
        details: "Plain birthday date; a birthday matching the selected date shows the animated cake icon on the right",
        colored: <BirthdayElement birthday={new Date("1995-07-11")} selectedDate={new Date("2026-07-11")} name="Sarah" />,
        cutoff: <BirthdayElement birthday={new Date("1995-07-11")} selectedDate={new Date("2026-07-11")} name="Sarah" />
    },
    {
        componentName: "NotesElement",
        details: "Tap or hover the latest note to open all notes below it",
        colored: (
            <NotesElement
                notes={[
                    "Guest requested a quiet table away from the kitchen.",
                    "Severe shellfish allergy; kitchen confirmation is required.",
                ]}
            />
        ),
        cutoff: (
            <NotesElement notes={[
                "Guest requested a quiet table away from the kitchen.",
                "This deliberately long latest note demonstrates the single-line cutoff in a narrow table column.",
            ]}/>
        )
    },
    {
        componentName: "RecommendationsInformationElement",
        details: "Single very long message — truncates to 1 line, click to wrap/expand",
        colored: <RecommendationsInformationElement messages={[{ de: "Zimmerupgrade auf eine Penthouse Suite mit privatem Jacuzzi anbieten", en: "Offer room upgrade to a penthouse suite with a private jacuzzi" }]} locale="de" />,
        cutoff: <RecommendationsInformationElement messages={[{ de: "Zimmerupgrade auf eine Penthouse Suite mit privatem Jacuzzi anbieten", en: "Offer room upgrade to a penthouse suite with a private jacuzzi" }]} locale="de" />
    },
    {
        componentName: "RecommendationsSuccessElement",
        details: "Single very long message — truncates to 1 line, click to wrap/expand",
        colored: <RecommendationsSuccessElement messages={[{ de: "Gern gesehener Stammgast und langjähriges Mitglied unseres VIP-Treueprogramms", en: "Frequent returning guest and long-standing member of our VIP loyalty program" }]} locale="de" />,
        cutoff: <RecommendationsSuccessElement messages={[{ de: "Gern gesehener Stammgast und langjähriges Mitglied unseres VIP-Treueprogramms", en: "Frequent returning guest and long-standing member of our VIP loyalty program" }]} locale="de" />
    },
    {
        componentName: "RecommendationsWarningElement",
        details: "Single very long message — truncates to 1 line, click to wrap/expand",
        colored: <RecommendationsWarningElement messages={[{ de: "Achtung: Late Checkout bis 18 Uhr angefragt wegen spätem Flug", en: "Warning: Late checkout requested until 6 PM due to a late flight departure" }]} locale="de" />,
        cutoff: <RecommendationsWarningElement messages={[{ de: "Achtung: Late Checkout bis 18 Uhr angefragt wegen spätem Flug", en: "Warning: Late checkout requested until 6 PM due to a late flight departure" }]} locale="de" />
    },
    {
        componentName: "ReservationStatusElement",
        details: "Green dot status: Confirmed",
        colored: <ReservationStatusElement status={ReservationStatus.CONFIRMED} />,
        cutoff: <ReservationStatusElement status={ReservationStatus.CONFIRMED} />
    },
    {
        componentName: "ReservationStatusElement",
        details: "Orange dot status: Pending",
        colored: <ReservationStatusElement status={ReservationStatus.PENDING} />,
        cutoff: <ReservationStatusElement status={ReservationStatus.PENDING} />
    },
    {
        componentName: "ReservationStatusElement",
        details: "Red dot status: Cancelled",
        colored: <ReservationStatusElement status={ReservationStatus.CANCELLED} />,
        cutoff: <ReservationStatusElement status={ReservationStatus.CANCELLED} />
    },
    {
        componentName: "ReservationStatusElement",
        details: "Grey dot status: Completed",
        colored: <ReservationStatusElement status={ReservationStatus.COMPLETED} />,
        cutoff: <ReservationStatusElement status={ReservationStatus.COMPLETED} />
    },
    {
        componentName: "ReservationsElement",
        details: "Chevron and person-count spacing remain fixed",
        colored: (
            <ReservationsElement
                reservations={[RESERVATION_TIMELINE_ITEMS[1]]}
                now={RESERVATION_DEMO_NOW}
                onSelect={() => undefined}
            />
        ),
        cutoff: (
            <ReservationsElement
                reservations={[{
                    ...RESERVATION_TIMELINE_ITEMS[1],
                    serviceName: "Very long signature treatment name",
                }]}
                now={RESERVATION_DEMO_NOW}
                onSelect={() => undefined}
            />
        )
    },
    {
        componentName: "ReservationsElement (restaurant)",
        details: "Restaurant priority: time, meal period, table, then restaurant; the long restaurant name truncates first",
        colored: (
            <ReservationsElement
                reservations={[RESTAURANT_RESERVATION_ITEM]}
                now={RESERVATION_DEMO_NOW}
                onSelect={() => undefined}
            />
        ),
        cutoff: (
            <ReservationsElement
                reservations={[RESTAURANT_RESERVATION_ITEM]}
                now={RESERVATION_DEMO_NOW}
                onSelect={() => undefined}
            />
        )
    },
    {
        componentName: "ReservationsElement",
        details: "Already-started reservation renders at 50% opacity",
        colored: (
            <ReservationsElement
                reservations={[RESERVATION_TIMELINE_ITEMS[0]]}
                now={RESERVATION_DEMO_NOW}
                onSelect={() => undefined}
            />
        ),
        cutoff: null
    },
    {
        componentName: "ReservationsElement",
        details: "All eight supported reservation statuses with their shared status-dot colors; use Show more to inspect the full set",
        colored: (
            <ReservationsElement
                reservations={RESERVATION_STATUS_ITEMS}
                now={RESERVATION_DEMO_NOW}
                onSelect={() => undefined}
            />
        ),
        cutoff: null
    },
    {
        componentName: "ReservationsElement",
        details: "Collapsed timeline shows one past and three upcoming reservations; includes Show more/less, Add, and More → Cancel all",
        colored: (
            <ReservationsElement
                reservations={RESERVATION_TIMELINE_ITEMS}
                now={RESERVATION_DEMO_NOW}
                onSelect={() => undefined}
                onAdd={() => undefined}
                onCancelAll={() => undefined}
            />
        ),
        cutoff: null
    },
    {
        componentName: "StayStatusElement",
        details: "Checking in stay status with stay dates (Green)",
        colored: (
            <StayStatusElement
                stayStatus="CHECKING_IN"
                startDate="2026-07-13"
                endDate="2026-07-16"
            />
        ),
        cutoff: (
            <StayStatusElement
                stayStatus="CHECKING_IN"
                startDate="2026-07-13"
                endDate="2026-07-16"
            />
        )
    },
    {
        componentName: "StayStatusElement",
        details: "Checking out stay status (Grey-blue)",
        colored: <StayStatusElement stayStatus="CHECKING_OUT" />,
        cutoff: <StayStatusElement stayStatus="CHECKING_OUT" />
    },
    {
        componentName: "StayStatusElement",
        details: "In-House stay status (Blue)",
        colored: <StayStatusElement stayStatus="STAYING" />,
        cutoff: <StayStatusElement stayStatus="STAYING" />
    },
    {
        componentName: "StayStatusElement",
        details: "Upcoming stay status (Warning Orange/Yellow)",
        colored: <StayStatusElement stayStatus="UPCOMING" />,
        cutoff: <StayStatusElement stayStatus="UPCOMING" />
    }
];

export function UiElementTestPage() {
    // PageShell bounds the tab at 100dvh and clips it, so this page has to own its own scroller
    return (
        <Container maxWidth="xl" sx={{ py: 4, overflowY: "auto" }}>
            <Card sx={{ p: 4, borderRadius: 2, boxShadow: "0 4px 20px rgba(0,0,0,0.05)" }}>
                <Typography variant="h5" sx={{ mb: 3, fontWeight: 600 }}>
                    UI Elements Playground
                </Typography>

                <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                    {/* Grid Header */}
                    <Box
                        sx={{
                            display: "grid",
                            gridTemplateColumns: "180px 320px 100px minmax(180px, 1fr)",
                            gap: 2,
                            pb: 1,
                            borderBottom: theme => `1px solid ${theme.palette.divider}`,
                            fontWeight: 600,
                            color: "text.secondary",
                            fontSize: "0.875rem",
                        }}
                    >
                        <Box>Component</Box>
                        <Box>Colored</Box>
                        <Box>Cutoff</Box>
                        <Box>Details</Box>
                    </Box>

                    {/* Grid Rows */}
                    {ITEMS.map((item, index) => {
                        return (
                            <Box
                                key={index}
                                sx={{
                                    display: "grid",
                                    gridTemplateColumns: "180px 320px 100px minmax(180px, 1fr)",
                                    gap: 2,
                                    alignItems: "center",
                                    py: 1.5,
                                    borderBottom: theme => `1px solid ${theme.palette.divider}`,
                                    "&:last-child": {
                                        borderBottom: "none",
                                    },
                                }}
                            >
                                {/* Column 1 — Component Name (wraps automatically) */}
                                <Box sx={{ fontWeight: 500, color: "text.secondary", fontSize: "0.9rem", wordBreak: "break-word" }}>
                                    {item.componentName}
                                </Box>

                                {/* Column 2 — default / colored */}
                                <Box sx={{ minWidth: 0, overflow: "hidden" }}>{item.colored}</Box>

                                {/* Column 3 — cutoff (100px max) */}
                                <Box sx={{ minWidth: 0, width: "100px", overflow: "hidden" }}>
                                    {item.cutoff}
                                </Box>

                                {/* Column 4 — Details */}
                                <Box
                                    sx={{
                                        fontSize: "0.9rem",
                                        color: "text.secondary",
                                        overflow: "hidden",
                                        textOverflow: "ellipsis",
                                        whiteSpace: "nowrap",
                                    }}
                                >
                                    {item.details}
                                </Box>
                            </Box>
                        );
                    })}
                </Box>
            </Card>
        </Container>
    );
}
